import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { ProductEvolution } from '../services/investmentService';
import TimeFilter, { TimeFilterOption } from './TimeFilter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ProductEvolutionChartProps {
  data: ProductEvolution[];
}

const ProductEvolutionChart: React.FC<ProductEvolutionChartProps> = ({ data }) => {
  const [selectedFilter, setSelectedFilter] = useState<TimeFilterOption>('MAX');
  
  // Filtrar dados baseado no filtro selecionado
  const filteredData = useMemo(() => {
    if (selectedFilter === 'MAX') {
      return data;
    }
    
    const now = new Date();
    let daysBack = 0;
    
    switch (selectedFilter) {
      case '30D':
        daysBack = 30;
        break;
      case '60D':
        daysBack = 60;
        break;
      case '90D':
        daysBack = 90;
        break;
      case '180D':
        daysBack = 180;
        break;
      case '1Y':
        daysBack = 365;
        break;
      case '2Y':
        daysBack = 730;
        break;
      case '5Y':
        daysBack = 1825;
        break;
    }
    
    const cutoffDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
    
    return data.map(product => ({
      ...product,
      evolution: product.evolution.filter(point => new Date(point.date) >= cutoffDate)
    }));
  }, [data, selectedFilter]);
  
  // Mostrar apenas as últimas 10 leituras quando o filtro MAX está ativo
  const limitedData = selectedFilter === 'MAX' 
    ? filteredData.map(product => ({
        ...product,
        evolution: product.evolution.slice(-10)
      }))
    : filteredData;
  // Gerar cores únicas para cada produto
  const colors = [
    'rgb(59, 130, 246)',   // Blue
    'rgb(16, 185, 129)',   // Green
    'rgb(245, 101, 101)',  // Red
    'rgb(251, 191, 36)',   // Yellow
    'rgb(139, 92, 246)',   // Purple
    'rgb(236, 72, 153)',   // Pink
    'rgb(14, 165, 233)',   // Sky
    'rgb(34, 197, 94)',    // Emerald
  ];

  // Obter todas as datas únicas
  const allDates = new Set<string>();
  limitedData.forEach(product => {
    product.evolution.forEach(point => {
      allDates.add(point.date);
    });
  });
  const sortedDates = Array.from(allDates).sort();

  const chartData = {
    labels: sortedDates.map(date => new Date(date).toLocaleDateString('pt-PT')),
    datasets: limitedData.map((product, index) => {
      const color = colors[index % colors.length];
      const backgroundColor = color.replace('rgb', 'rgba').replace(')', ', 0.1)');
      
      // Criar array de valores para todas as datas (usar variação percentual)
      const values = sortedDates.map(date => {
        const point = product.evolution.find(p => p.date === date);
        return point ? point.variation_percent : 0;
      });

      return {
        label: `${product.product_name} (${product.area_name})`,
        data: values,
        borderColor: color,
        backgroundColor: backgroundColor,
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        spanGaps: true, // Conectar pontos mesmo com valores 0
      };
    })
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        }
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            if (value === null || value === undefined) return '';
            
            // Não ocultar percentagens - são apenas variações relativas, não valores sensíveis
            
            return `${context.dataset.label}: ${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Data'
        },
        grid: {
          display: false
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Variação (%)'
        },
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return value >= 0 ? '+' + value.toFixed(1) + '%' : value.toFixed(1) + '%';
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  return (
    <div>
      <div className="mb-4">
        <TimeFilter 
          selectedFilter={selectedFilter} 
          onFilterChange={setSelectedFilter}
          className="justify-center"
        />
      </div>
      <div className="h-80">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ProductEvolutionChart;
